/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { ValidationRule } from "antd/lib/form/Form"
import { Form } from "grab-ui"
import { Input } from "@duxton-web/ui"
import { ChevronRightUniversal } from "@duxton-web/icons"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { compact } from "lodash"
import { OutletStore, OutletStoreV2 } from "types/models/menu"
import { getCurrencyInfo } from "utils/currency"
import {
  DeferredValidator,
  eventValueToCleanNumericInput,
  getValidationProps,
  newPriceDiffValidator,
} from "utils/form_helper"
import { storageHelper } from "utils/storage"
import { FormItemProps } from "antd/lib/form"
import { getAdvancedPricingValidationConfig } from "constants/service_based_menu"
import { useEffect } from "react"
import useFEConfig from "hooks/useFEConfig"
import useMenuContext from "hooks/useMenuContext"

interface PriceRange {
  min: string
  max: string
}

interface PriceInputFieldProps extends FormComponentProps {
  disabled?: boolean
  priceValue?: number | null
  outlet?: OutletStore | null
  outletV2?: OutletStoreV2 | null
  priceValidator: DeferredValidator
  onChangePriceInMin?: (priceInMin: string) => void
  onPriceRangeClick?: () => void
  priceRange?: PriceRange
  formItemProps?: FormItemProps
  fieldID?: string
  validationType: "item" | "modifier"
  enableTroyNewMerchantEndpoint?: boolean
  required: boolean
}

interface PriceRangeFieldProps {
  disabled?: boolean
  onClick?: () => void
  priceRange: PriceRange
}

function PriceRangeField({
  disabled,
  onClick,
  priceRange,
}: PriceRangeFieldProps) {
  const textMap = useTranslationsMapTyped({
    pricePlaceholder: "menu.drawers.item.placeholders.price",
  })
  return (
    <div
      css={css`
        position: relative;
        .overlay {
          position: absolute;
          right: 0;
          top: 0;
          width: 100%;
          height: 100%;
          ${!disabled ? "cursor: pointer;" : ""}
          display: flex;
        }
      `}
      onClick={!disabled ? onClick : undefined}
    >
      <Input
        autoComplete="off"
        addonBefore={storageHelper.getUserCurrency()}
        placeholder={textMap.pricePlaceholder}
        disabled={disabled}
        value={`${priceRange.min} - ${priceRange.max}`}
        readOnly
      />
      <div className="overlay">
        <div
          css={css`
            flex: 1 1;
          `}
        />
        <div
          css={css`
            flex: 0 0;
            padding-right: 12px;
          `}
        >
          <ChevronRightUniversal />
        </div>
      </div>
    </div>
  )
}

function PriceInputField({
  disabled,
  form,
  priceValue,
  outlet,
  priceValidator,
  onChangePriceInMin,
  onPriceRangeClick,
  priceRange,
  formItemProps = {},
  fieldID = "priceInMin",
  validationType,
  outletV2,
  enableTroyNewMerchantEndpoint,
  required,
}: PriceInputFieldProps) {
  const textMap = useTranslationsMapTyped({
    labelPrice: "menu.drawers.item.labels.price",
    pricePlaceholder: "menu.drawers.item.placeholders.price",
    errorPriceDifference: "menu.errors.advancedPricing.priceDifference",
    errorPriceDeviation: "menu.errors.advancedPricing.priceDeviation",
    errorPriceExceed: "menu.errors.price.exceedConfigPriceLimit",
  })
  const { getFieldDecorator } = form
  const feConfig = useFEConfig()
  const { isServiceBasedMenuEnabled } = useMenuContext()
  const isPriceRangeView =
    isServiceBasedMenuEnabled &&
    !!priceRange &&
    priceRange.min !== priceRange.max &&
    feConfig.showAdditionalPriceDrawerLink
  // if (!outlet) {
  //   // this check exists in the runtime level instead of the TS level
  //   // because we don't want the `outlet` signature to clash with
  //   // what is returned by our store's `useSelector` (which is OutletStore | null | undefined)
  //   const missingOutletErrMsg = new Error(
  //     "Missing outlet. Currency might not be loaded correctly.",
  //   )
  //   // eslint-disable-next-line no-console
  //   console.error(missingOutletErrMsg)
  //   Sentry.captureException(missingOutletErrMsg)
  // }
  const advancedPricingValidationConfig = getAdvancedPricingValidationConfig(
    validationType,
    feConfig,
  )
  const maxPriceDifference =
    advancedPricingValidationConfig?.maxPriceDifference ?? 0
  const maxPriceDeviation =
    advancedPricingValidationConfig?.maxPriceDeviation ?? 0
  const currencyCode = enableTroyNewMerchantEndpoint
    ? outletV2?.currency?.code
    : outlet?.currency

  useEffect(() => {
    // handle case when there's already error in additional price on render
    // this can happen if there is a change in max price deviation value in grabx
    if (isPriceRangeView) {
      form.validateFields()
    }
  }, [isPriceRangeView])

  const modifiedValidator = (
    rule: ValidationRule,
    baseValue: any,
    callback: (error?: string) => void,
  ) => {
    // Base value
    const modifiedValue = baseValue

    // Call the original validator with the modified value
    newPriceDiffValidator(
      maxPriceDeviation,
      textMap.errorPriceDeviation.replace(
        /<limit>/i,
        String(maxPriceDeviation * 100),
      ),
      form.getFieldValue(fieldID),
    )(rule, modifiedValue, callback)
  }

  return (
    <Form.Item
      label={textMap.labelPrice}
      required={required}
      {...getValidationProps(form, [fieldID], true).priceInMin}
      {...formItemProps}
    >
      {isPriceRangeView && (
        <PriceRangeField
          data-testid="price-range"
          disabled={disabled}
          onClick={onPriceRangeClick}
          priceRange={priceRange!}
        />
      )}
      {getFieldDecorator(fieldID, {
        initialValue: priceValue,
        getValueFromEvent: (e: any) => {
          const newValue = eventValueToCleanNumericInput(
            e,
            getCurrencyInfo(currencyCode)?.decimal,
          )
          onChangePriceInMin?.(newValue)
          return newValue
        },
        validateFirst: true,
        rules: compact<ValidationRule>([
          {
            validator: priceValidator.onChangeRuleErrorClearerValidator,
          },
          ...(maxPriceDifference
            ? [
                {
                  validator: newPriceDiffValidator(
                    maxPriceDifference,
                    textMap.errorPriceDifference.replace(
                      /<limit>/i,
                      String(maxPriceDifference * 100),
                    ),
                    priceValue,
                  ),
                },
              ]
            : []),
          // need to check if max price deviation is valid before opening the additional price drawer
          ...((isPriceRangeView && maxPriceDeviation
            ? [
                {
                  validator: (
                    rule: ValidationRule,
                    value: any,
                    callback: (error?: string) => void,
                  ) => {
                    modifiedValidator(rule, priceRange?.max, callback)
                  },
                },
                {
                  validator: (
                    rule: ValidationRule,
                    value: any,
                    callback: (error?: string) => void,
                  ) => {
                    modifiedValidator(rule, priceRange?.min, callback)
                  },
                },
              ]
            : []) as ValidationRule[]),
          // isEdit && {
          //   validator: handleValidatePriceChange,
          // },
        ]),
      })(
        <Input
          autoComplete="off"
          data-testid="input-price"
          addonBefore={storageHelper.getUserCurrency()}
          placeholder={textMap.pricePlaceholder}
          disabled={disabled}
          style={isPriceRangeView ? { display: "none" } : {}}
        />,
      )}
    </Form.Item>
  )
}

export default PriceInputField
